import { useDispatch, useSelector } from "react-redux";
import { Location, NavigateFunction, useNavigate } from "react-router-dom";

import navigationItemReferences from "../constants/navigation-item-references";
import pages from "../constants/pages";
import {
	NavItem,
	selectGroupChildren,
	selectInitialState,
	selectNavState,
	selectOrganizationChildren,
	selectPluginChildren,
	selectRoleChildren,
	setExpandedItems,
	setNavState,
	setSelectedItem,
} from "../redux/navigationSlice";
import { RootState } from "../redux/store";
import {
	selectOrganizationIdentityProvider,
	selectOrganizationSsoAndScim,
} from "../redux/userSlice";
import useAuthorization from "./useAuthorization";

interface UserRow {
  id: string;
  firstname?: string;
  lastname?: string;
  email: string;
  parentResourceId?: string;
  parentName?: string;
}

interface ResourceRow {
  id?: string;
  name: string;
  active?: boolean;
  isSuperAdmin?: boolean;
  hasHomePage?: boolean;
  children?: NavItem[];
}

interface ViewUserParams {
  row: UserRow;
  id?: string;
}

interface ResourceParams {
  id?: string;
  row?: ResourceRow;
}

interface ReconstructParams {
  id?: string;
  name?: string;
  isActive?: boolean;
  isSuperAdmin?: boolean;
  resourceId?: string;
  platformId?: string | number;
  children?: NavItem[];
}

interface UseCoAppNavigationReturn {
  openResourceEditPage: (
    params: ResourceParams,
    openedFrom: number,
    parentName: string
  ) => void;
  getUpdatedNavState: (
    openedFrom: number,
    parentName: string,
    params: ResourceParams
  ) => NavItem[];
  getDeepestNavState: (
    params: ViewUserParams,
    openedFrom: number,
    userId: string
  ) => NavItem[];
  getUserNavState: (params: ViewUserParams) => [NavItem[], NavItem];
  handleOpenResourceHomePage: (
    openedFrom: number,
    parentName: string,
    params: ReconstructParams | null,
    isRebuild?: boolean
  ) => void;
  openRuleWizard: () => void;
  openScimWizard: (doNavigation?: boolean) => void;
  reconstructParentPage: (pathItems: string[]) => void;
  reconstructSubResourcePage: (pathItems: string[], location: Location) => void;
  reconstructUserPage: (pathItems: string[], location: Location) => void;
  updateCoAppNavigation: (
    isDeleteEvent?: boolean,
    openedFrom?: number,
    nameInput?: string,
    pathname?: string
  ) => void;
  viewUserPage: (
    params: ViewUserParams,
    openedFrom: number,
    parentId?: string,
    parentName?: string
  ) => void;
  viewUserPageFromUserManagement: (params: ViewUserParams) => void;
}

export default function useCoAppNavigation(): UseCoAppNavigationReturn {
	const dispatch = useDispatch();
	const navigate: NavigateFunction = useNavigate();
	const navState = useSelector((state: RootState) => selectNavState(state));
	const navInitialState = useSelector(selectInitialState);
	const groupChildren = useSelector((state: RootState) =>
		selectGroupChildren(state)
	);
	const pluginChildren = useSelector((state: RootState) =>
		selectPluginChildren(state)
	);
	const organizationChildren = useSelector((state: RootState) =>
		selectOrganizationChildren(state)
	);
	const roleChildren = useSelector((state: RootState) =>
		selectRoleChildren(state)
	);
	const organizationSsoAndScim = useSelector((state: RootState) =>
		selectOrganizationSsoAndScim(state)
	);
	const identityProviderName = useSelector((state: RootState) =>
		selectOrganizationIdentityProvider(state)
	);
	const hasUserAccess = useAuthorization(
		["ACCESS_MP", "MANAGE_USERS_AND_GROUPS"],
		true
	);

	const updateCoAppNavigation = (
		isDeleteEvent: boolean = false,
		openedFrom?: number,
		nameInput: string = "",
		pathname: string = ""
	): void => {
		if (openedFrom === undefined) return;

		const newNavState: NavItem[] = [...navState];
		if (!isDeleteEvent) {
			const copyGroupChildren: NavItem[] = [
				...newNavState[openedFrom].children,
			].slice(1);
			copyGroupChildren.unshift({
				id: Math.random(),
				name: nameInput,
				children: [],
				isGroupingLabel: true,
			} as NavItem);

			newNavState[openedFrom] = {
				...newNavState[openedFrom],
				children: copyGroupChildren,
				isChangeEvent: true,
				isStillSelected: true,
				selectedResource: "Users",
				isNameChangeEvent: true,
			};
		} else {
			newNavState[openedFrom] = {
				...newNavState[openedFrom],
				children: [],
				isStillSelected: true,
				selectedResource: "",
			};
		}
		dispatch(setNavState(newNavState));

		if (!isDeleteEvent) {
			navigate(pathname, { state: { name: nameInput }, replace: true });
		}
	};

	const getDeepestNavState = (
		params: ViewUserParams,
		openedFrom: number,
		userId: string
	): NavItem[] => {
		const newNavState: NavItem[] = [...navState];
		newNavState[openedFrom] = {
			...newNavState[openedFrom],
		};
		let selectedObj: NavItem;
		if (newNavState[openedFrom].children.length > 0) {
			newNavState[openedFrom].children = newNavState[openedFrom].children.map(
				(resource) => {
					if (resource.name === "Users") {
						const temp = { ...resource };
						selectedObj = {
							id: Math.random(),
							name:
                params.row.firstname && params.row.lastname
                	? `${params.row.firstname} ${params.row.lastname}`
                	: params.row.email,
							children: [],
							keepOpen: true,
							isClickable: true,
							isUserNavigationItem: true,
							page: pages.profile + userId,
							firstname: params.row.firstname,
							lastname: params.row.lastname,
						};
						temp.children = [selectedObj];
						return temp;
					}
					return resource;
				}
			);
		} else {
			selectedObj = {
				id: Math.random(),
				name:
          params.row.firstname && params.row.lastname
          	? `${params.row.firstname} ${params.row.lastname}`
          	: params.row.email,
				children: [],
				keepOpen: true,
				isClickable: true,
				isUserNavigationItem: true,
				page: pages.profile + userId,
				firstname: params.row.firstname,
				lastname: params.row.lastname,
			};
			const resourceChildren = openedFrom === 7 ? groupChildren : roleChildren;
			let temp = resourceChildren.map((child) => {
				if (child.name === "Add/Remove IdP Groups" && organizationSsoAndScim) {
					return {
						...child,
						name: identityProviderName
							? `Add/Remove ${identityProviderName} Groups`
							: "Add/Remove IdP Groups",
						page: handlePageGeneration(
							child.name,
							openedFrom === 7 ? "Groups" : "Roles",
							params.row.parentResourceId
						),
						keepOpen: true,
						isClickable: true,
					} as NavItem;
				} else if (child.name === "Users") {
					return {
						...child,
						page: handlePageGeneration(
							child.name,
							openedFrom === 7 ? "Groups" : "Roles",
							params.row.parentResourceId
						),
						children: [selectedObj],
						keepOpen: true,
						isClickable: true,
					} as NavItem;
				} else {
					return {
						...child,
						page: handlePageGeneration(
							child.name,
							openedFrom === 7 ? "Groups" : "Roles",
							params.row.parentResourceId
						),
						keepOpen: true,
						isClickable: true,
					} as NavItem;
				}
			});
			temp.unshift({
				id: Math.random(),
				name: params.row.parentName || "",
				children: [],
				isGroupingLabel: true,
			} as NavItem);
			temp = temp.filter((child) => {
				if (!organizationSsoAndScim && child.name.includes("Add/Remove")) {
					return false;
				} else {
					return true;
				}
			});
			newNavState[openedFrom].children = temp;
		}
		return newNavState;
	};

	const viewUserPage = (
		params: ViewUserParams,
		openedFrom: number,
		parentId?: string,
		parentName?: string
	): void => {
		const newNavState: NavItem[] = [...navState];
		newNavState[openedFrom] = {
			...newNavState[openedFrom],
		};
		let selectedObj: NavItem | undefined;
		newNavState[openedFrom].children = newNavState[openedFrom].children.map(
			(resource) => {
				if (resource.name === "Users") {
					const temp = { ...resource };
					selectedObj = {
						id: Math.random(),
						name:
              params.row.firstname && params.row.lastname
              	? `${params.row.firstname} ${params.row.lastname}`
              	: params.row.email,
						children: [],
						keepOpen: true,
						isClickable: true,
						isUserNavigationItem: true,
						page: pages.profile + params.row.id,
						firstname: params.row.firstname,
						lastname: params.row.lastname,
						parentResourceId: parentId,
						parentName: parentName,
					} as NavItem;
					temp.children = [selectedObj];
					return temp;
				}
				return resource;
			}
		);

		if (selectedObj) {
			dispatch(setSelectedItem(selectedObj));
			dispatch(
				setExpandedItems([
					(
						openedFrom + navigationItemReferences.navItemIndexToNavItemIdOffset
					).toString(),
					openedFrom === 7 ? "15" : "18",
				])
			);
			dispatch(setNavState(newNavState));
			navigate(
				pages.profile + params.row.id + `?from=${openedFrom === 7 ? "g" : "r"}`,
				{ state: selectedObj }
			);
		}
	};

	const getUserNavState = (params: ViewUserParams): [NavItem[], NavItem] => {
		const newNavState: NavItem[] = [...navState];
		const selectedObj: NavItem = {
			id: Math.random(),
			name:
        params.row.firstname && params.row.lastname
        	? `${params.row.firstname} ${params.row.lastname}`
        	: params.row.email,
			children: [],
			keepOpen: true,
			isClickable: true,
			firstname: params.row.firstname,
			lastname: params.row.lastname,
			email: params.row.email,
			page: pages.profile + params.row.id,
		};
		newNavState[6] = {
			...newNavState[6],
			children: [selectedObj],
		};
		return [newNavState, selectedObj];
	};

	const viewUserPageFromUserManagement = (params: ViewUserParams): void => {
		const newNavState: NavItem[] = [...navState];
		const selectedObj: NavItem = {
			id: Math.random(),
			name:
        params.row.firstname && params.row.lastname
        	? `${params.row.firstname} ${params.row.lastname}`
        	: params.row.email,
			children: [],
			keepOpen: true,
			isClickable: true,
			firstname: params.row.firstname,
			lastname: params.row.lastname,
			email: params.row.email,
			page: pages.profile + params.id,
		};
		newNavState[6] = {
			...newNavState[6],
			children: [selectedObj],
		};
		dispatch(setNavState(newNavState));
		dispatch(setSelectedItem(selectedObj));
		dispatch(setExpandedItems(["8"]));
		navigate(pages.profile + params.id + "?from=um", { state: selectedObj });
	};

	const getResourceChildren = (navItemId: number): NavItem[] => {
		switch (navItemId) {
			case navigationItemReferences.organizationNavItemIndex:
				return organizationChildren;
			case navigationItemReferences.groupNavItemIndex:
				return groupChildren;
			case navigationItemReferences.roleNavItemIndex:
				return roleChildren;
			case navigationItemReferences.pluginNavItemIndex:
				return pluginChildren;
			default:
				return [];
		}
	};

	const handlePageGeneration = (
		childName: string,
		parentName: string,
		childId?: string
	): string => {
		switch (childName) {
			case "Users":
				return parentName === "Roles"
					? `/settings/roles/${childId}/users`
					: `/settings/groups/${childId}/users`;
			case "Permissions":
				return `/settings/roles/${childId}/permissions`;
			case "Add/Remove IdP Groups":
				return parentName === "Roles"
					? `/settings/roles/${childId}/sso`
					: `/settings/groups/${childId}/sso`;
			case "Groups":
				return `/settings/plugins/${childId}/groups`;
			case "Rules":
				return `/settings/groups/${childId}/rules`;
			case "Plugins":
				return `/settings/groups/${childId}/plugins`;
			default:
				return "/settings/organization";
		}
	};

	const handleParentPageGeneration = (
		parentName: string,
		parentId: string
	): string => {
		switch (parentName) {
			case "Roles":
				return `/settings/roles/${parentId}`;
			case "Groups":
				return `/settings/groups/${parentId}`;
			case "Plugins":
				return `/settings/plugins/${parentId}`;
			default:
				return "/settings/organization";
		}
	};

	const getUpdatedNavState = (
		openedFrom: number,
		parentName: string,
		params: ResourceParams
	): NavItem[] => {
		const newNavState: NavItem[] = [...navState];
		const resourceChildren = getResourceChildren(openedFrom);

		newNavState[openedFrom] = {
			...newNavState[openedFrom],
			children: [
				{
					id: Math.random(),
					name: params.row?.name || "",
					children: [],
					isGroupingLabel: true,
					hasHomePage: params.row?.hasHomePage,
					page: "",
				},
				...resourceChildren
					.map((child) => {
						if (
							child.name === "Add/Remove IdP Groups" &&
              organizationSsoAndScim
						) {
							return {
								...child,
								name: identityProviderName
									? `Add/Remove ${identityProviderName} Groups`
									: "Add/Remove IdP Groups",
								page: handlePageGeneration(child.name, parentName, params.id),
								keepOpen: true,
								isClickable: true,
							};
						} else {
							return {
								...child,
								page: handlePageGeneration(child.name, parentName, params.id),
								keepOpen: true,
								isClickable: true,
							};
						}
					})
					.filter((child) => {
						if (!organizationSsoAndScim && child.name.includes("Add/Remove")) {
							return false;
						} else {
							return true;
						}
					}),
			],
			isParentChangeEvent: true,
		};
		return newNavState;
	};

	const openResourceEditPage = (
		params: ResourceParams,
		openedFrom: number,
		parentName: string
	): void => {
		const newNavState: NavItem[] = [...navState];
		const resourceChildren = getResourceChildren(openedFrom);
		newNavState[openedFrom] = {
			...newNavState[openedFrom],
			children: [
				{
					id: Math.random(),
					name: params.row?.name || "",
					children: [],
					isActive: params.row?.active,
					isGroupingLabel: true,
					isSuperAdmin: params.row?.isSuperAdmin,
					hasHomePage: false,
					page: "",
				},
				...resourceChildren
					.map((child) => {
						if (
							child.name === "Add/Remove IdP Groups" &&
              organizationSsoAndScim
						) {
							return {
								...child,
								name: identityProviderName
									? `Add/Remove ${identityProviderName} Groups`
									: "Add/Remove IdP Groups",
								page: handlePageGeneration(child.name, parentName, params.id),
								keepOpen: true,
								isClickable: true,
							};
						} else {
							return {
								...child,
								page: handlePageGeneration(child.name, parentName, params.id),
								keepOpen: true,
								isClickable: true,
							};
						}
					})
					.filter((child) => {
						if (!organizationSsoAndScim && child.name.includes("Add/Remove")) {
							return false;
						} else {
							return true;
						}
					}),
			],
		};

		dispatch(setNavState(newNavState));
		dispatch(setSelectedItem(newNavState[openedFrom].children[1]));
		dispatch(
			setExpandedItems([
				(
					openedFrom + navigationItemReferences.navItemIndexToNavItemIdOffset
				).toString(),
			])
		);
		navigate(newNavState[openedFrom].children[1].page || "", {
			state: newNavState[openedFrom].children[0],
		});
	};

	const handleOpenResourceHomePage = (
		openedFrom: number,
		parentName: string,
		params: ReconstructParams | null,
		isRebuild: boolean = false
	): void => {
		if (!params) {
			console.error(
				"Cannot open resource home page: params is null or undefined"
			);
			return;
		}

		const newNavState: NavItem[] = [...navState];
		const resourceChildren = getResourceChildren(openedFrom);
		const tempResourceChildren = [...resourceChildren];

		const gridParams = params as ReconstructParams & {
      row?: { name?: string; active?: boolean; isCustomerManaged?: boolean; platformId?: string | number };
      id?: string;
    };

		const resourceId = isRebuild ? params.resourceId : gridParams.id;
		if (!resourceId) {
			console.error(
				"Cannot open resource home page: resource ID is null or undefined"
			);
			return;
		}

		tempResourceChildren[0] = {
			...tempResourceChildren[0],
			page: handlePageGeneration(
				tempResourceChildren[0].name,
				parentName,
				resourceId
			),
			isClickable: true,
			isGroupingLabel: false,
			hasHomePage: false,
		};

		const name =
      (isRebuild ? params.name : params.name ?? gridParams.row?.name) ||
      "Unknown";
		const isActive = isRebuild ? params.isActive : params.isActive ?? gridParams.row?.active;
		const isSuperAdmin = isRebuild ? params.isSuperAdmin : undefined;

		const isPluginCustomerManaged = gridParams.row?.isCustomerManaged === true;
		const platformId = gridParams.platformId ?? gridParams.row?.platformId;
		const parentPage =
      parentName === "Plugins" && !isPluginCustomerManaged
      	? pages.pluginGroups(String(resourceId))
      	: handleParentPageGeneration(parentName, String(resourceId));

		newNavState[openedFrom] = {
			...newNavState[openedFrom],
			children: [
				{
					id: Math.random(),
					name: name,
					children: tempResourceChildren,
					isActive: isActive,
					isGroupingLabel: true,
					isSuperAdmin: isSuperAdmin,
					page: parentPage,
					hasHomePage: true,
					resourceId: resourceId,
					platformId,
				},
			],
		};

		dispatch(setNavState(newNavState));
		dispatch(setSelectedItem(newNavState[openedFrom].children[0]));
		dispatch(setExpandedItems(["12", "999"]));
		if (!isRebuild) {
			const navChild = newNavState[openedFrom].children[0];
			navigate(navChild.page || "", {
				state: {
					...navChild,
					pluginId: String(resourceId),
					platformId,
				},
			});
		}
	};

	const openScimWizard = (doNavigation: boolean = true): void => {
		const newNavState: NavItem[] = [...navState];
		const organizationChildren = getResourceChildren(5);
		newNavState[5] = {
			...newNavState[5],
			children: organizationChildren,
			keepOpen: true,
		};
		dispatch(setNavState(newNavState));
		dispatch(setSelectedItem(newNavState[5].children[0]));
		dispatch(setExpandedItems(["7"]));
		if (doNavigation) navigate(pages.scimWizard);
	};

	const reconstructParentPage = (pathItems: string[]): void => {
		let reconstructedPage = `/${pathItems[0]}/${pathItems[1]}`;
		let foundNode: NavItem | undefined;

		if (pathItems[0] === "rules" && pathItems[1].startsWith("library")) {
			if (pathItems[1] !== "library") {
				reconstructedPage = "/rules/library";
			}
		}

		const isRuleEditing =
      pathItems[0] === "rules" && !pathItems[1].includes("library");

		try {
			if (reconstructedPage === "/rules/trash") {
				if (
					!navInitialState.selectedItem ||
          !navInitialState.selectedItem.children
				) {
					console.error(
						"Invalid navigation state: selectedItem or its children is undefined"
					);
					return;
				}
				foundNode = navInitialState.selectedItem.children[0];
			} else if (reconstructedPage === "/rules/new") {
				foundNode = navInitialState.navState[1];
			} else if (reconstructedPage === "/rules/library" || isRuleEditing) {
				foundNode = navInitialState.selectedItem;
			} else {
				foundNode = navInitialState.navState.filter(
					(node) => node.page === reconstructedPage
				)[0];
			}

			if (!foundNode) {
				console.error(
					`Could not find navigation node for path: ${reconstructedPage}`
				);
				return;
			}

			const tempSelected: NavItem = {
				id: foundNode.id,
				name: foundNode.name,
				children: foundNode.children,
				page: foundNode.page,
			};

			dispatch(setSelectedItem(tempSelected));
			const isLibraryPage = ["/rules/library", "/rules/trash"].includes(
				reconstructedPage
			);
			if (
				(isLibraryPage || isRuleEditing) &&
        reconstructedPage !== "/rules/new"
			) {
				dispatch(setExpandedItems(["3"]));
			} else {
				dispatch(setExpandedItems([]));
			}
		} catch (error) {
			console.error("Error in reconstructParentPage:", error);
		}
	};

	const reconstructSubResourcePage = (
		pathItems: string[],
		location: Location
	): void => {
		if (!location || !location.state) {
			console.error("Missing location state in reconstructSubResourcePage");
			return;
		}

		const reconstructedBasePage = `/${pathItems[0]}/${pathItems[1]}`;
		const reconstructedFullPage = `/${pathItems[0]}/${pathItems[1]}/${pathItems[2]}/${pathItems[3]}`;
		const foundParentNode = navInitialState.navState.filter(
			(node) => node.page === reconstructedBasePage
		)[0];

		if (!foundParentNode) {
			console.error(
				"Could not find parent node for page:",
				reconstructedBasePage
			);
			return;
		}

		const parentResourceName = pathItems[1].substring(0, pathItems[1].length - 1);
		let subResourceName = pathItems[3];
		subResourceName =
      subResourceName.charAt(0).toUpperCase() + subResourceName.slice(1);

		const childrenEntry = Object.entries(navInitialState).find(
			([key]) => key === `${parentResourceName}Children`
		);
		if (!childrenEntry || !childrenEntry[1]) {
			console.error(
				"Could not find children for resource:",
				parentResourceName
			);
			return;
		}

		const children = childrenEntry[1] as NavItem[];

		const state = location.state as any;
		if (!state.id || !state.name) {
			console.error("Missing required state properties in location state");
			return;
		}

		const params: ResourceParams = {
			id: pathItems[2],
			row: {
				id: state.id,
				name: state.name,
				children:
          location.pathname.includes("plugins") && state.children
          	? state.children
          	: [],
				hasHomePage: state.hasHomePage || false,
			},
		};

		const sel = children.find((child) => child.name === subResourceName);
		if (!sel) {
			console.error("Could not find child with name:", subResourceName);
			return;
		}

		const tempSelected: NavItem = {
			id: sel.id,
			name: state.name,
			children: [],
			page: reconstructedFullPage,
		};

		const parent = pathItems[1].charAt(0).toUpperCase() + pathItems[1].slice(1);
		const openedFrom = pathItems[1].includes("group")
			? navigationItemReferences.groupNavItemIndex
			: pathItems[1].includes("plugins")
				? navigationItemReferences.pluginNavItemIndex
				: navigationItemReferences.roleNavItemIndex;
		const newNavState = getUpdatedNavState(openedFrom, parent, params);

		if (location.pathname.includes("plugins") && state.children.length > 0) {
			const tempNavState = [...newNavState];
			let newChildren: NavItem[] = [];
			if (tempNavState[openedFrom].children[0].children.length === 0) {
				newChildren.push({
					...state,
					children: state.children,
				});
			} else {
				newChildren = [...state.children];
			}
			tempNavState[openedFrom] = {
				...tempNavState[openedFrom],
				children: newChildren,
			};
			dispatch(setNavState(tempNavState));
			dispatch(setSelectedItem(state.children[0]));
			dispatch(setExpandedItems(["12", Math.random().toString()]));
		} else {
			dispatch(setNavState(newNavState));
			dispatch(setExpandedItems([foundParentNode.id.toString()]));
			dispatch(setSelectedItem(tempSelected));
		}
	};

	const reconstructUserPage = (
		pathItems: string[],
		location: Location
	): void => {
		if (!hasUserAccess) return;
		const from = location.search.split("=")[1];
		const userObj: ViewUserParams = {
			row: {
				...(location.state as any),
			},
		};
		let restoredNavState: NavItem[];
		switch (from) {
			case "g":
				restoredNavState = getDeepestNavState(userObj, 7, pathItems[2]);
				dispatch(setNavState(restoredNavState));
				dispatch(setSelectedItem(location.state as NavItem));
				dispatch(setExpandedItems(["9", "14"]));
				break;
			case "r":
				restoredNavState = getDeepestNavState(userObj, 8, pathItems[2]);
				dispatch(setNavState(restoredNavState));
				dispatch(setSelectedItem(location.state as NavItem));
				dispatch(setExpandedItems(["10", "17"]));
				break;
			case "um":
				const navStateAndObj = getUserNavState(userObj);
				dispatch(setNavState(navStateAndObj[0]));
				dispatch(setSelectedItem(location.state as NavItem));
				dispatch(setExpandedItems(["8"]));
				break;
			default:
				const defaultNavState = getUserNavState(userObj);
				dispatch(setNavState(defaultNavState[0]));
				dispatch(setSelectedItem(defaultNavState[1]));
				dispatch(setExpandedItems(["8"]));
				break;
		}
	};

	const openRuleWizard = (): void => {
		navigate(pages.newRuleBuilder);
		dispatch(setExpandedItems([""]));
		dispatch(setSelectedItem(navInitialState.navState[1]));
	};

	return {
		openResourceEditPage,
		getUpdatedNavState,
		getDeepestNavState,
		getUserNavState,
		handleOpenResourceHomePage,
		openRuleWizard,
		openScimWizard,
		reconstructParentPage,
		reconstructSubResourcePage,
		reconstructUserPage,
		updateCoAppNavigation,
		viewUserPage,
		viewUserPageFromUserManagement,
	};
}
